import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {ofType, createEffect, Actions, ROOT_EFFECTS_INIT} from '@ngrx/effects';

import {
  catchError,
  concatMap,
  debounceTime,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import {AuthActions} from '../actions';
import {AppState, DoNothingAction, selectAuthState} from '../../../core.state';
import {forkJoin, of} from 'rxjs';
import {AlertController} from '@ionic/angular';
import {AuthSelectors} from '../selectors';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '../../../../shared/services';
import {AuthService} from '../../../services';
import {UtilsService} from '../../../../shared/services';
import {
  InitDataType,
  MAIN_URL_REDIRECT,
  STORE_DB_NAME_KEY
} from '../../../@types';
import {LocalStorageService} from '../../../storages/local-storage.service';
import {UtentiService} from '../../../services';
import {AppError} from '../../../error-handlers/app-error';
import {
  V1ApiHttpErrorResponse,
  WEBSERVICE_APP_VERSION_ERROR
} from '../../../interceptors/v1-http-error-interceptor.service';
import {TorneiService} from '../../../services/tornei.service';
import {SessionStorageService} from '../../../storages/session-storage.service';

@Injectable()
export class AuthEffects {

  /******************************************
   ********** INIT DATA EFFECTS *************
   ******************************************/

  initAppProcedure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        withLatestFrom(this.store.select(selectAuthState)),
        tap(() => {
          // Imposto la lingua di default a 'it'
          this.translateService.setDefaultLang('it');
        }),
        concatMap(() => [
          // AuthActions.ControlloVersioneApp(),
          AuthActions.CheckAuthToken()
        ]),
      )
  );

  checkAuthToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.CheckAuthToken),
        withLatestFrom(
          this.store.pipe(select(AuthSelectors.selectIsAuthenticated)),
          this.store.pipe(select(AuthSelectors.selectAuthToken)),
        ),
        switchMap(([, isAuthenticated, Token]) => {

          // se l'utente non risulta autenticato non c'è niente da controllare
          if (!isAuthenticated) {
            return of(AuthActions.CheckAuthTokenNull());
          }

          // se il token non è presente per sicurezza eseguo il logout
          if (!Token) {
            return of(AuthActions.Logout());
          } else {
            // se il token è presente ne controllo la validità sul server
            return this.authService.get$().pipe(
              map(() => {
                // reindirizzo alla home solo se sono su una delle pagine di login
                if (!!this.router.url.match(/login/)) {
                  const mainUrl = this.localStorageService.getMainUrl();
                  this.router.navigateByUrl(mainUrl);
                }

                // Token ok procedo con il refresh dei dati
                return AuthActions.CheckAuthTokenSuccess();
              }),
              // In caso di qualunque tipo di errore in questa fase eseguo il logout per sicurezza
              catchError((error) => {
                console.log(error);
                return of(AuthActions.Logout());
              })
            );
          }
        })
      )
  );

  checkAuthTokenSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.CheckAuthTokenSuccess),
        withLatestFrom(
          this.store.select(AuthSelectors.selectAuthUserData),
        ),
        map(([, userAuthData]) => {
          const Lingua = UtilsService.getLangString('Italiano');
          this.translateService.use(Lingua);
          return AuthActions.InitData();
        })
      )
  );

  initData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.InitData),
        switchMap(() => forkJoin({
          classifica: this.torneiService.getClassificaAnnuale$(),
          utenti: of([]),
          tornei: of([]),
          anniTornei: this.torneiService.getAnniTornei$()
        }).pipe(
          map((data: InitDataType) => AuthActions.InitDataSuccess({...data})),
          catchError(err => of(AuthActions.InitDataError({error: err})))
        ))
      )
  );

  /*initDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.InitDataSuccess),
        tap(() => {

          // reindirizzo alla home solo se sono su una delle pagine di login
          if (!!this.router.url.match(/login/)) {
            this.store.dispatch(HomeActions.LoadHomeData({}));
            this.router.navigateByUrl(MAIN_URL_REDIRECT, {replaceUrl: true});
          }

        })
      ),
    {dispatch: false}
  );*/

  initDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.InitDataError),
        map(({error}) => {
          this.notificationService.danger('Errore inizializzazione dati');
          return AuthActions.Logout();
        })
      )
  );

  /******************************************
   ************* LOGIN EFFECTS **************
   ******************************************/

  /*** Action sul pulsante 'Accedi' nella LoginPage ***/
  loginMain$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.Login),
        debounceTime(500),
        switchMap((action) => this.authService.loginUser({
          ...action.loginFormData,
          dbName: action.loginFormData.dbName.toLowerCase()
        }).pipe(
          map((userAuthData) => AuthActions.LoginSuccess({
            userAuthData,
            loginFormData: action.loginFormData
          })),
          catchError(({error}) => of(AuthActions.LoginError({error})))
        ))
      )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LoginSuccess),
        withLatestFrom(
          this.store.pipe(select(selectAuthState)),
        ),
        map(([{userAuthData, loginFormData}, authState]) => {

          // Imposto la lingua dell'operatore
          const locale = 'it';
          this.translateService.use(locale);

          // Salvo il nome del db name in utilizzo
          this.sessionStorageService.setItem(STORE_DB_NAME_KEY, loginFormData.dbName);

          // Salvataggio dei dati nello storage di riferimento in base al parametro 'rememberMe'
          this.authService.storageAuthData({...authState, userAuthData, loginFormData}, loginFormData.rememberMe);

          // reindirizzo alla home solo se sono su una delle pagine di login
          if (!!this.router.url.match(/login/)) {
            this.router.navigateByUrl(MAIN_URL_REDIRECT, {replaceUrl: true});
            return AuthActions.InitData();
          }

          return DoNothingAction();
        })
      )
  );

  loginError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.LoginError),
        debounceTime(300),
        tap((error: { error: V1ApiHttpErrorResponse }) => {
          this.authService.removeStoredAuthData(); // Controllare se inserirlo o no
          const message = error.error.message || this.translateService.instant('TOASTER.LOGIN_ERROR');
          if (error.error.errorCode !== WEBSERVICE_APP_VERSION_ERROR) {
            this.notificationService.danger(message);
            throw new AppError('LOGIN_ERROR', error.error.message, error.error);
          }
        })
      ),
    {dispatch: false}
  );

  alertLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.AlertLogout),
        switchMap(() => this.alertController.create({
            cssClass: 'large-alert-text',
            header: this.translateService.instant('ALERT.WARNING'),
            message: this.translateService.instant('ALERT.LOGOUT'),
            buttons: [
              {
                text: this.translateService.instant('LABEL.YES'),
                role: 'ok'
              },
              {
                text: this.translateService.instant('LABEL.NO'),
                role: 'cancel'
              }
            ]
          })
        ),
        switchMap((alert) => {
          alert.present();
          return alert.onWillDismiss();
        }),
        map((res) => (res.role === 'ok') ? AuthActions.Logout() : DoNothingAction())
      )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.Logout),
        tap(() => {

          this.authService.removeStoredAuthData();

          const dbName = this.sessionStorageService.getItem(STORE_DB_NAME_KEY);
          this.router.navigateByUrl(`/login/${dbName}`);
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private store: Store<AppState>,
    private authService: AuthService,
    private utentiService: UtentiService,
    private torneiService: TorneiService,
    private alertController: AlertController,
    private translateService: TranslateService,
    private notificationService: NotificationService,
  ) {
  }

}
